import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import logoBase64 from "../../utils/logoBase64";

export default function OverallProgressPDF({
  progressList,
  getProgressStatus,
  student,
}) {
  const exportToPDF = () => {
    const doc = new jsPDF("p", "pt");
    const pageWidth = doc.internal.pageSize.getWidth();

    const imageWidth = 160;
    const imageHeight = 120;
    const imageX = (pageWidth - imageWidth) / 2;
    let currentY = 30;
    doc.addImage(logoBase64, "PNG", imageX, currentY, imageWidth, imageHeight);
    currentY += imageHeight + 15;

    currentY += 16;

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(25, 180, 222); // #19b4de
    const title = "Heet Student Tracking System (HETRAS)";
    const titleX = (pageWidth - doc.getTextWidth(title)) / 2;
    doc.text(title, titleX, currentY);

    currentY += 20;

    doc.setFontSize(14);
    doc.setTextColor(211, 194, 13); // #d3c20d
    const subtitle = "Study Progress Report";
    const subtitleX = (pageWidth - doc.getTextWidth(subtitle)) / 2;
    doc.text(subtitle, subtitleX, currentY);

    currentY += 25;

    if (student) {
      const start = dayjs(student.startDate);
      const end = dayjs(student.endDate);
      const years = end.diff(start, "year");

      const pronoun =
        student.gender?.toLowerCase() === "female" ? "her" : "his";

      const studentSummary = `${student.name} is a student at ${
        student.university
      } in the ${student.program} program for ${years} year${
        years > 1 ? "s" : ""
      }, from ${start.format("MMMM D, YYYY")} to ${end.format(
        "MMMM D, YYYY"
      )}. ${
        pronoun.charAt(0).toUpperCase() + pronoun.slice(1)
      } study progress is followed below. `;

      doc.setFont("Helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);

      const studentLines = doc.splitTextToSize(studentSummary, pageWidth - 80); // 40pt margin each side
      doc.text(studentLines, 40, currentY);
      currentY += studentLines.length * 15 + 10;
    }

    // --- Overall Summary ---
    const total = progressList.length;
    const statusCounts = { Good: 0, Moderate: 0, Poor: 0 };

    progressList.forEach((progress) => {
      const status = getProgressStatus(progress);
      if (statusCounts[status] !== undefined) statusCounts[status]++;
    });

    const summary = [
      ["Good", `${((statusCounts.Good / total) * 100).toFixed(1)}%`],
      ["Moderate", `${((statusCounts.Moderate / total) * 100).toFixed(1)}%`],
      ["Poor", `${((statusCounts.Poor / total) * 100).toFixed(1)}%`],
    ];

    autoTable(doc, {
      theme: "plain",
      head: [["Progress Status", "Percentage"]],
      body: summary,
      startY: currentY,
      styles: { fontStyle: "bold" },
      headStyles: { fillColor: [25, 180, 222], textColor: [255, 255, 255] }, // #19b4de
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    currentY = doc.lastAutoTable.finalY + 30;

    // --- Monthly progress ---
    const monthlyData = {};
    progressList.forEach((progress) => {
      const month = dayjs(progress.progressDate).format("MMMM YYYY");
      if (!monthlyData[month]) {
        monthlyData[month] = { Good: 0, Moderate: 0, Poor: 0, total: 0 };
      }
      const status = getProgressStatus(progress);
      monthlyData[month][status]++;
      monthlyData[month].total++;
    });

    const monthRows = Object.entries(monthlyData).map(([month, data]) => [
      month,
      `${((data.Good / data.total) * 100 || 0).toFixed(1)}%`,
      `${((data.Moderate / data.total) * 100 || 0).toFixed(1)}%`,
      `${((data.Poor / data.total) * 100 || 0).toFixed(1)}%`,
    ]);

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Monthly Progress", 40, currentY);

    autoTable(doc, {
      theme: "striped",
      head: [["Month", "Good", "Moderate", "Poor"]],
      body: monthRows,
      startY: currentY + 15,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [25, 180, 222], textColor: [255, 255, 255] },
    });

    currentY = doc.lastAutoTable.finalY + 30;

    // --- Detailed Table ---
    const tableColumn = [
      "#",
      "Previous",
      "Current",
      "Next",
      "Supervisor",
      "Thesis",
      "Date",
      "Status",
    ];
    const tableRows = progressList.map((progress, index) => [
      index + 1,
      progress.previous?.slice(0, 40) || "N/A",
      progress.currently?.slice(0, 40) || "N/A",
      progress.next?.slice(0, 40) || "N/A",
      progress.meetSupervisor || "N/A",
      progress.submitThesis || "N/A",
      progress.progressDate || "N/A",
      getProgressStatus(progress),
    ]);

    doc.setFontSize(14);
    doc.text("Detailed Report", 40, currentY);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: currentY + 15,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [211, 194, 13], textColor: [0, 0, 0] }, // #d3c20d
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    // --- Footer with pagination & copyright ---
    const pageCount = doc.getNumberOfPages();
    doc.setFontSize(10);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 80, 820);
      doc.text(
        `© ${dayjs().year()} HETRAS | Heet Student Tracking System`,
        40,
        820
      );
    }

    // --- Save PDF ---
    doc.save(`${student.name}_study_progress.pdf`);
  };

  return (
    <button className="btn btn-black rounded-5" onClick={exportToPDF}>
      <span className="fa fa-file-pdf"></span> Export to PDF
    </button>
  );
}
